import ListView, { ListViewItem } from '@ingka/list-view';
import { selectBuCode } from '@repo/api-adapters/selectors/customer-assets.selectors';
import { selectPaymentDetails } from '@repo/api-adapters/selectors/payment.selectors';
import { useStoreDetails } from '@repo/hooks';
import { translate } from '@repo/phrase';
import { formatDateWithWeekday } from '@repo/utils';

import { TELEMETRY_LOGS, logTelemetry } from '@/lib/telemetry.lib';

import { MODAL } from '@/components/modals/asset-modal.types';
import type { Step } from '@/components/modals/asset-payment-modal/asset-payment-modal.component.tsx';
import {
    useAssetPaymentEditable,
    useManualPayments,
} from '@/components/modals/asset-payment-modal/asset-payment-modal.hooks.ts';

import { getServiceEndpoint } from '@/api/helpers.api.ts';
import { useAsset, useAssetParams } from '@/hooks/asset.hooks.ts';
import { analyticsEvents, trackAnalyticsEvent } from '@/utils/analytics.utils.ts';
import { getAppConfig } from '@/utils/window.utils.ts';

interface Props {
    setStep: (step: Step) => void;
    isEditEnabled: boolean;
    closeModal: () => void;
}

export const AssetPaymentView = ({ setStep, isEditEnabled, closeModal }: Props) => {
    const { assetId, assetType } = useAssetParams();
    const { data } = useAsset(assetType, assetId);
    const { paymentTiming, paymentDueDate } = selectPaymentDetails(data);
    const assetBuCode = selectBuCode(data) ?? '';
    const config = getAppConfig();
    const storeDetailsBaseUrl = getServiceEndpoint(config.api.services.classicSalja, 'storeDetails');
    const { data: storeDetails } = useStoreDetails(storeDetailsBaseUrl, assetBuCode);
    const storeTimezone = storeDetails?.TimeZone ?? 'UTC';
    const dateFormat = config.dateTimeFormat;
    const paymentTranslation = paymentTiming
        ? translate('asset_payment_modal_ON_DATE')
        : translate('asset_payment_modal_no_payment_info');

    const paymentDueDateFormattedWithWeekday = paymentDueDate
        ? formatDateWithWeekday(paymentDueDate, dateFormat, storeTimezone)
        : '';

    const { daysBetween } = useAssetPaymentEditable();
    const { canRegisterPayment, isManualPaymentsAvailable, manualPaymentsFragment } = useManualPayments();

    return (
        <ListView id="asset-payment-list" size="medium">
            <ListViewItem
                title={translate('asset_payment_settings')}
                description={
                    `${paymentTranslation}, ` +
                    `${translate('asset_payment_modal_before')} ${paymentDueDateFormattedWithWeekday}`
                }
                emphasised
                data-testid="asset-payment-button"
                showDivider={false}
                {...(isEditEnabled && {
                    control: 'navigational',
                    controlIcon: 'chevronRight',
                    onClick: () => {
                        setStep('EDIT');

                        trackAnalyticsEvent({
                            eventName: analyticsEvents.paymentDateOptionsClicked,
                            custom: {
                                assetId,
                                hasPaymentDateOptions: daysBetween >= 0,
                            },
                        });
                    },
                })}
                size="medium"
            />
            {isManualPaymentsAvailable && canRegisterPayment && manualPaymentsFragment && (
                <ListViewItem
                    title={translate('asset_payment_modal_manual_payments_register_payment')}
                    emphasised
                    control="navigational"
                    controlIcon="chevronRight"
                    onClick={() => {
                        manualPaymentsFragment.openRegisterPayment(assetId);
                        logTelemetry('info', TELEMETRY_LOGS.ModalOpened, {
                            modal: { name: MODAL.PAYMENT_REGISTER },
                        });
                        closeModal();
                    }}
                />
            )}
            {isManualPaymentsAvailable && manualPaymentsFragment && (
                <ListViewItem
                    title={translate('asset_payment_modal_manual_payments_payment_history')}
                    emphasised
                    control="navigational"
                    controlIcon="chevronRight"
                    onClick={() => {
                        logTelemetry('info', TELEMETRY_LOGS.ModalOpened, {
                            modal: { name: MODAL.PAYMENT_HISTORY },
                        });
                        manualPaymentsFragment.openPaymentsHistory(assetId);
                        closeModal();
                    }}
                />
            )}
        </ListView>
    );
};
